import { Plus, Pencil, Trash2, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Pagination } from '../../components/ui/Pagination'
import { PageTitle } from '../../components/ui/PageTitle'
import { api } from '../../lib/api'

type Offer = { id: number; code: string; discountType: string; value: number; minOrder: number; usageLimit: number; usedCount: number; status: string }

export function OffersPage() {
  const [offers, setOffers] = useState<Offer[]>([])
  const [confirmId, setConfirmId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('All')
  const [type, setType] = useState('All')
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)

  const load = () => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (status !== 'All') params.set('status', status)
    if (type !== 'All') params.set('discountType', type)
    params.set('page', String(page))
    params.set('limit', String(limit))
    api.get(`/admin/offers?${params}`).then(({ data }) => {
      setOffers(data.offers)
      setTotal(data.total ?? data.offers.length)
    })
  }

  useEffect(() => { load() }, [search, status, type, page, limit])
  useEffect(() => { setPage(1) }, [search, status, type, limit])

  const remove = async (id: number) => {
    await api.delete(`/admin/offers/${id}`)
    setConfirmId(null)
    load()
  }

  return (
    <div className="space-y-6">
      <PageTitle title={`Offers (${total})`} subtitle="Manage cart coupons and promo codes."
        action={<NavLink to="/offers/new"><Button icon={Plus}>Add Offer</Button></NavLink>} />

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <input
            className="w-full rounded-lg border border-stone-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-mango-orange focus:ring-4 focus:ring-mango-yellow/25"
            placeholder="Search by code…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-mango-orange" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="All">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        <select className="rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-mango-orange" value={type} onChange={e => setType(e.target.value)}>
          <option value="All">All Types</option>
          <option value="flat">Flat</option>
          <option value="percent">Percent</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-stone-50 text-xs font-semibold uppercase tracking-wide text-stone-500">
            <tr>{['Code', 'Type', 'Value', 'Min Order', 'Limit', 'Used', 'Status', ''].map(h => (
              <th key={h} className="px-4 py-3 text-left">{h}</th>
            ))}</tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {offers.map(o => (
              <tr key={o.id} className="hover:bg-stone-50">
                <td className="px-4 py-3 font-mono font-semibold">{o.code}</td>
                <td className="px-4 py-3 capitalize">{o.discountType}</td>
                <td className="px-4 py-3">{o.discountType === 'percent' ? `${o.value}%` : `₹${o.value}`}</td>
                <td className="px-4 py-3">₹{o.minOrder}</td>
                <td className="px-4 py-3">{o.usageLimit}</td>
                <td className="px-4 py-3">{o.usedCount}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${o.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>{o.status}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <NavLink to={`/offers/${o.id}/edit`}><Pencil className="h-4 w-4 text-stone-500 hover:text-leaf-dark" /></NavLink>
                    <button onClick={() => setConfirmId(o.id)}><Trash2 className="h-4 w-4 text-stone-400 hover:text-red-500" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {offers.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-stone-400">No offers found</td></tr>}
          </tbody>
        </table>
        <Pagination
          limit={limit}
          onLimitChange={setLimit}
          onPageChange={setPage}
          page={page}
          total={total}
        />
      </div>

      {confirmId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-80 rounded-2xl bg-white p-6 shadow-xl">
            <p className="font-semibold text-stone-800">Delete this offer?</p>
            <p className="mt-1 text-sm text-stone-500">This action cannot be undone.</p>
            <div className="mt-4 flex gap-3">
              <button onClick={() => setConfirmId(null)} className="flex-1 rounded-lg border border-stone-200 py-2 text-sm font-medium hover:bg-stone-50">Cancel</button>
              <button onClick={() => remove(confirmId)} className="flex-1 rounded-lg bg-red-500 py-2 text-sm font-medium text-white hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
